import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import { translatedMessage } from '../../service/LanguageService';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import DataTableUtils from '../../utilities/DataTableUtils';
import DeleteModalComponent from '../DeleteModalComponent';
import EnumService from '../../service/EnumService';
import QuestionEditDialog from './QuestionEditDialog';
import { CompetitionService } from '../../service/CompetitionService';
import DOMPurify from 'dompurify';

const QuestionListComponent = (props) => {
    const [questions, setQuestions] = useState([]);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [questionTypes, setQuestionTypes] = useState([]);

    const [filters] = useState({
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.IN },
        active: { value: null, matchMode: FilterMatchMode.IN }
    });

    const toast = useRef(null);
    const dt = useRef(null);
    const { auth } = useAuth();
    const navigate = useNavigate();

    const competitionService = useMemo(() => new CompetitionService(), []);

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    useEffect(() => {
        setIsLoading(true);

        const getQuestionTypes = async () => {
            if (!questionTypes || questionTypes.length === 0) {
                await EnumService.getEnumByName('QuestionType').then((types) => setQuestionTypes(types));
            }
        };

        getQuestionTypes();
        setQuestions(props.questions);
        setIsLoading(false);

        let _selectedQuestion = props.questions.find((_p) => _p.id === selectedQuestion?.id);
        setSelectedQuestion(_selectedQuestion);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.questions, props.canEdit, competitionService]);

    const showNewDialog = () => {
        setSelectedQuestion(null);
        setEditDialogVisible(true);
    };

    const showEditDialog = (question) => {
        setSelectedQuestion(question);
        setEditDialogVisible(true);
    };

    const hideEditDialog = () => {
        setSelectedQuestion(null);
        setEditDialogVisible(false);
    };

    const showDeleteDialog = (question) => {
        setSelectedQuestion(question);
        setDeleteDialogVisible(true);
    };

    const hideDeleteDialog = () => {
        setDeleteDialogVisible(false);
    };

    const deleteQuestion = () => {
        competitionService
            .deleteQuestion(selectedQuestion.id)
            .then(() => {
                refreshData();
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
            .finally(() => {
                setDeleteDialogVisible(false);
                setSelectedQuestion(null);
            });
    };

    const afterSaveQuestion = async (savedQuestion, isNew) => {
        if (isNew) {
            setSelectedQuestion(savedQuestion);
            setEditDialogVisible(true);
        }
        await refreshData();
    };

    const refreshData = async () => {
        competitionService
            .getCompetitionSubjects(props.competition?.id)
            .then((_subjects) => {
                let _proposals = _subjects.map((subject) => subject.proposals).flat();
                let _selectedProposal = _proposals.find((_p) => _p.id === props.proposal?.id);
                setQuestions(_selectedProposal.questions);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                {props.permissions.canEditSubjectProposals && (
                    <div className="my-2">
                        <Button label={translatedMessage('competition.subjectProposal.question.add')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showNewDialog} type={'button'} />
                    </div>
                )}
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {};

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {props.permissions.canEditSubjectProposals && (hasPermission('COMPETITION_EDIT') || hasPermission('COMPETITION_VIEW')) && (
                    <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-info m-1"
                        onClick={() => showEditDialog(rowData)}
                        tooltip={translatedMessage('generic.edit')}
                        tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                        type={'button'}
                    />
                )}
                {props.permissions.canEditSubjectProposals && (
                    <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger m-1"
                        onClick={() => showDeleteDialog(rowData)}
                        disabled={rowData.id < 1000}
                        tooltip={translatedMessage('generic.delete')}
                        tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                        type={'button'}
                    />
                )}
            </div>
        );
    };

    const questionTypeTemplate = (rowData) => {
        return <div>{questionTypes?.find((type) => type.value === rowData.questionType)?.label}</div>;
    };

    const questionContentTemplate = (rowData) => {
        return (
            <div className="filed-value">
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rowData.questionContent) }} />
            </div>
        );
    };

    const commentTemplate = (rowData) => {
        return (
            <div className="filed-value">
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rowData.comment) }} />
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className='mt-5'>
                    <h5 className="mb-1 font-weight-normal">{translatedMessage('competition.subjectProposal.questions')}</h5>
                </div>
                <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                <DataTable
                    ref={dt}
                    value={questions}
                    dataKey="id"
                    paginator
                    rows={DataTableUtils.defalRowsPerPage()}
                    rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                    className="datatable-responsive pcn-datatable"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                    emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                    responsiveLayout="stack"
                    // filterDisplay="row"
                    // filters={filters}
                    // globalFilterFields={['name', 'type', 'active']}
                >
                    <Column field="name" header={translatedMessage('generic.name')} sortable headerStyle={{ width: '8%', minWidth: '8rem' }} />
                    <Column field="questionType" body={questionTypeTemplate} header={translatedMessage('competition.subjectProposal.question.type')} sortable headerStyle={{ width: '15%', minWidth: '8rem' }} />
                    <Column field="questionContent" body={questionContentTemplate} header={translatedMessage('competition.subjectProposal.question.questionContent')} sortable headerStyle={{ width: '20%', minWidth: '10rem' }} />
                    <Column field="comment" body={commentTemplate} header={translatedMessage('competition.subjectProposal.question.comment')} sortable headerStyle={{ width: '35%', minWidth: '10rem' }} />
                    <Column field="score" header={translatedMessage('competition.subjectProposal.question.score')} sortable headerStyle={{ width: '5%', minWidth: '5rem' }} />
                    <Column field="answers.length" header={translatedMessage('competition.question.answersNumber')} sortable headerStyle={{ width: '5%', minWidth: '5rem' }} />
                    <Column body={actionBodyTemplate} />
                </DataTable>

                <QuestionEditDialog
                    value={selectedQuestion}
                    visible={editDialogVisible}
                    visibleSetter={setEditDialogVisible}
                    afterSave={afterSaveQuestion}
                    questionTypes={questionTypes}
                    proposal={props.proposal}
                    competition={props.competition}
                    permissions={props.permissions}
                />

                {deleteDialogVisible && <DeleteModalComponent visible={deleteDialogVisible} item={selectedQuestion ? selectedQuestion.name : ''} closeDialog={hideDeleteDialog} deleteRecord={() => deleteQuestion()} />}
            </>
        );
    }
};

export default QuestionListComponent;
